import { DateTime } from 'luxon'

/**
 * Maps common Windows timezone names (e.g. profile `timeZoneId`) to IANA names
 * that Luxon understands. Mirrors the backend's normalizeTimezone so the
 * frontend computes/renders dates in the same zone the API uses.
 */
const WINDOWS_TO_IANA: Record<string, string> = {
  'Eastern Standard Time': 'America/New_York',
  'Central Standard Time': 'America/Chicago',
  'Mountain Standard Time': 'America/Denver',
  'Pacific Standard Time': 'America/Los_Angeles',
  'Atlantic Standard Time': 'America/Halifax',
  'Newfoundland Standard Time': 'America/St_Johns',
  'Alaskan Standard Time': 'America/Anchorage',
  'Hawaiian Standard Time': 'Pacific/Honolulu',
  'GMT Standard Time': 'Europe/London',
  'Central European Time': 'Europe/Berlin',
  'Eastern European Time': 'Europe/Kiev',
  'India Standard Time': 'Asia/Kolkata',
  'China Standard Time': 'Asia/Shanghai',
  'Japan Standard Time': 'Asia/Tokyo',
  'Australian Eastern Standard Time': 'Australia/Sydney',
  'Australian Central Standard Time': 'Australia/Adelaide',
  'Australian Western Standard Time': 'Australia/Perth',
  'W. Australia Standard Time': 'Australia/Perth',
  'Tasmania Standard Time': 'Australia/Hobart',
  'Cen. Australia Standard Time': 'Australia/Adelaide',
  'AUS Central Standard Time': 'Australia/Darwin',
  'AUS Eastern Standard Time': 'Australia/Sydney',
  'E. Australia Standard Time': 'Australia/Brisbane',
}

/** Normalize a Windows or IANA timezone name to an IANA name Luxon can use. */
export const normalizeTimezone = (timezone?: string | null): string => {
  if (!timezone) return DateTime.local().zoneName!
  return WINDOWS_TO_IANA[timezone] || timezone
}

// Module-scoped copy of the current user's zone so non-React code (e.g. an
// insight's module-level `load`) can resolve the same zone the components use.
// Published by AuthProvider whenever the authenticated user changes.
let currentZone = ''

export const setUserTimezone = (timezone?: string | null): void => {
  currentZone = timezone ? normalizeTimezone(timezone) : ''
}

export const getUserTimezone = (): string => currentZone || DateTime.local().zoneName!

/**
 * Short timezone abbreviation for a given instant in the user's zone, e.g.
 * 'EST' in winter / 'EDT' in summer. Computed per-instant so it stays correct
 * across daylight-saving transitions.
 */
export const tzAbbrev = (ms: number, zone: string = getUserTimezone()): string =>
  DateTime.fromMillis(ms, { zone }).toFormat('ZZZZ')
